import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PlugZap, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { PageHeader, Field, StatusBadge } from "@/components/app/ui";
import { ConnStatus } from "@/pages/DataSources";
import { ConnectDialog } from "@/components/discovery/ConnectDialog";
import { AssetPicker } from "@/components/discovery/AssetPicker";
import { PiiResults } from "@/components/discovery/PiiResults";
import { FlowSteps } from "@/components/discovery/FlowSteps";
import { api, errMsg, can, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RUNNING = ["Queued", "Running"];

function ScanCard({ s }) {
  const st = s.stats || {};
  const pct = st.assets_total ? Math.round(((st.assets_scanned || 0) / st.assets_total) * 100) : 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`scan-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-slate-500">{s.id}</span><StatusBadge status={s.status} /><span className="ml-auto text-xs text-slate-500">{fmtDateTime(s.created_at)} · {s.started_by}</span></div>
      {RUNNING.includes(s.status) && <Progress value={pct} className="mt-3 h-2" data-testid="scan-progress" />}
      <div className="mt-3 grid grid-cols-3 gap-3 text-xs md:grid-cols-6">
        <Field label="Assets">{`${st.assets_scanned || 0}/${st.assets_total || 0}`}</Field><Field label="With PII">{String(st.assets_with_pii ?? 0)}</Field>
        <Field label="Columns">{String(st.columns_scanned ?? 0)}</Field><Field label="PII columns">{String(st.pii_columns ?? 0)}</Field>
        <Field label="Rows sampled">{String(st.rows_sampled ?? 0)}</Field><Field label="Sample size">{String(s.sample_size)}</Field>
      </div>
      {s.errors?.length > 0 && <ul className="mt-3 space-y-1 rounded border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800" data-testid="scan-errors">{s.errors.slice(0, 8).map((e, i) => <li key={i}><b className="font-mono">{e.asset}</b>: {e.error}</li>)}</ul>}
    </div>
  );
}

export default function DataSourceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [scans, setScans] = useState([]);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("select");
  const [edit, setEdit] = useState(false);
  const [testing, setTesting] = useState(false);
  const load = useCallback(() => {
    api.get(`/data-sources/${id}`).then((r) => setS(r.data)).catch(() => nav("/data-sources"));
    api.get(`/data-sources/${id}/scans`).then((r) => setScans(r.data));
    api.get("/discovery/findings", { params: { source_id: id } }).then((r) => setRows(r.data));
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  const running = scans.some((x) => RUNNING.includes(x.status));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 2500); return () => clearInterval(t); }, [running, load]);
  const test = async () => { setTesting(true); try { const { data } = await api.post(`/data-sources/${id}/test`); setS(data); data.status === "Connected" ? toast.success("Connection OK") : toast.error(data.last_error); } catch (e) { toast.error(errMsg(e)); } setTesting(false); };
  const del = async () => { if (!window.confirm("Delete this data source, its stored credentials and open scan results?")) return; await api.delete(`/data-sources/${id}`); nav("/data-sources"); };
  if (!s) return null;
  const w = can(user, "datasource");
  const step = rows.some((r) => r.finding_id) ? 6 : rows.length ? 5 : scans.length ? 2 : s.status === "Connected" ? 1 : 0;
  return (
    <div>
      <button onClick={() => nav("/data-sources")} className="mb-2 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="back-to-sources"><ArrowLeft className="h-3 w-3" />All data sources</button>
      <PageHeader title={s.name} testId="page-title" subtitle={`${s.type_name} · ${s.environment} · ${s.auth_method}`}
        actions={w && <div className="flex gap-2">
          <Button variant="outline" onClick={test} disabled={testing} data-testid="test-connection-button"><PlugZap className="mr-1.5 h-4 w-4" />{testing ? "Testing…" : "Test connection"}</Button>
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-source-button"><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" onClick={del} data-testid="delete-source-button"><Trash2 className="h-4 w-4 text-rose-500" /></Button></div>} />
      <FlowSteps active={step} />
      <div className="mb-5 grid gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-4" data-testid="source-summary">
        <Field label="Connection"><ConnStatus s={s} /></Field>
        <Field label="Read-only check">{s.read_only_note || "Run a connection test"}</Field>
        <Field label="Server">{s.server}</Field><Field label="Last tested">{fmtDateTime(s.last_tested_at)}</Field>
        {s.last_error && <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800 md:col-span-4" data-testid="source-error">{s.last_error}</div>}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="select" data-testid="tab-select">Select & scan</TabsTrigger><TabsTrigger value="scans" data-testid="tab-scans">Scans ({scans.length})</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">PII findings ({rows.length})</TabsTrigger></TabsList>
        <TabsContent value="select" className="pt-3">{w ? <AssetPicker source={s} disabled={running} onStarted={() => { setTab("scans"); load(); }} /> : <p className="text-sm text-slate-500">Your role can view results but not run scans.</p>}</TabsContent>
        <TabsContent value="scans" className="space-y-3 pt-3">{scans.map((x) => <ScanCard key={x.id} s={x} />)}{!scans.length && <p className="text-sm text-slate-400">No scans yet.</p>}</TabsContent>
        <TabsContent value="results" className="pt-3"><PiiResults rows={rows} onChange={load} /></TabsContent>
      </Tabs>
      <ConnectDialog open={edit} existing={s} onClose={() => setEdit(false)} onSaved={(x) => { setEdit(false); setS(x); }} />
    </div>
  );
}
